import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '../components/nav';
import { ActivityItemView } from '../features/activities/ActivityPlayer';
import { clearProgress, readProgress, solvedCount, writeItem, type ItemState } from '../features/activities/progress';
import { MECHANIC_LABEL } from '../data/types';
import { useAppData } from '../state/AppData';

export function ActivityList() {
  const { activities, cities } = useAppData();
  // un reto que cubre más de una colección se marca: sus pasajes están repartidos por el libro
  const crosses = (ids: string[]) => ids.length > 1
    && new Set(ids.map((i) => cities.find((c) => c.collectionId === i)?.province)).size > 1;
  const [progress, setProgress] = useState(readProgress);
  const mechanics = [...new Set(activities.map((a) => a.mechanic))];
  return (
    <div className="page">
      <h1>Retos</h1>
      <p>
        {activities.length} retos repartidos en cuatro mecánicas. No hay puntuaciones, ni tiempos, ni clasificaciones: se trata de
        formular una hipótesis, comprobarla contra las fuentes y quedarse con la evidencia.
      </p>
      <p className="note">
        El progreso se guarda solo en este navegador, es privado y se puede borrar cuando quieras. «No lo sé» es
        una respuesta legítima y no penaliza.
      </p>
      {mechanics.map((m) => (
        <section key={m}>
          <h2>{MECHANIC_LABEL[m] ?? m}</h2>
          <ul className="itin-list">
            {activities.filter((a) => a.mechanic === m).map((a) => (
              <li key={a.id}>
                <h3><Link to={`/retos/${a.slug}`}>{a.title}</Link>{crosses(a.collectionIds) && <span className="badge badge--cross">cruza el libro</span>}</h3>
                {a.subtitle && <p className="itin-list__sub">{a.subtitle}</p>}
                <p>{a.summary}</p>
                <p className="itin-meta">
                  {a.items.length === 1 ? '1 ejercicio' : `${a.items.length} ejercicios`} · {a.estimatedMinutes} min aprox. · nivel {a.level}
                  {solvedCount(progress, a.id) > 0 && <> · {solvedCount(progress, a.id)} resuelto(s)</>}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p><button type="button" className="link-button" onClick={() => setProgress(clearProgress())}>Borrar mi progreso</button></p>
    </div>
  );
}

export function ActivityView() {
  const { slug } = useParams();
  const { activities } = useAppData();
  const [progress, setProgress] = useState(readProgress);
  const a = activities.find((x) => x.slug === slug);
  if (!a) {
    return (
      <div className="page">
        <h1>Reto no encontrado</h1>
        <p><Link to="/retos">Ver los retos publicados</Link></p>
      </div>
    );
  }
  const onResolved = (n: number, s: ItemState) => setProgress(writeItem(a.id, n, s));
  const solved = solvedCount(progress, a.id);
  return (
    <div className="page page--reto">
      <p className="itin__back"><Link to="/retos">← Todos los retos</Link></p>
      <p className="reto__mechanic">{MECHANIC_LABEL[a.mechanic] ?? a.mechanic}</p>
      <h1>{a.title}</h1>
      {a.subtitle && <p className="itin__sub">{a.subtitle}</p>}
      <p>{a.summary}</p>
      <p className="note">{a.instructions}</p>
      <h2>Qué se practica</h2>
      <ul>{a.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
      <p className="itin-meta" role="status">
        {solved} de {a.items.length} {a.items.length === 1 ? 'ejercicio resuelto' : 'ejercicios resueltos'} en este navegador
      </p>
      {a.items.map((item) => <ActivityItemView key={item.n} activity={a} item={item} onResolved={onResolved} />)}
      <h2>Para cerrar</h2>
      <p>{a.closing}</p>
      <p className="itin__meta">Revisión: {a.lastReviewed}</p>
    </div>
  );
}
